import { InventoryItemFragment } from '@epicuro-next/data/product';
import { CellData } from '@epicuro-next/features/config-strip/utilities/materials';
import { CellInventory } from '@epicuro-next/state/config-strip-state';
import { getUTCNow, getUTCNowPlusDays } from '@epicuro-next/utilities';
import { OrderType } from 'graphql-api/gql-api-interfaces';

export type AvailabilityColorRange = {
  start: number;
  end: number;
  color: string;
};

/**
 * Returns cell inventory
 * @param materialId
 */
export const getCellInventoryTotals =
  (
    materialsMatrix: Map<string, [CellData[]]>,
    sizesInventories: { [sizeId: string]: CellInventory },
  ) =>
  (materialId: string, row: number, col: number) =>
    getCellInventories(materialsMatrix, sizesInventories)(materialId, row, col)
      .totals;

export const getCellInventories =
  (
    materialsMatrix: Map<string, [CellData[]]>,
    sizesInventories: {
      [sizeId: string]: CellInventory;
    },
  ) =>
  (materialId: string, row: number, col: number): CellInventory => {
    const materialData = materialsMatrix.get(materialId);
    const cellData = materialData?.[row]?.[col];
    if (cellData) {
      const inventoriesAvailability = sizesInventories[cellData.productId] ?? {
        totals: 0,
        inventories: [],
        hasTrueDemandInventory: false,
      };
      const inventoriesTotal = inventoriesAvailability?.totals ?? 0;

      return {
        ...inventoriesAvailability,
        totals: inventoriesTotal,
      };
    }

    return { totals: 0, inventories: [], hasTrueDemandInventory: false };
  };

export const backOrdersOnly = (cellInventory: CellInventory) =>
  !!cellInventory.inventories.length &&
  cellInventory.inventories.every((inventory) => {
    return (
      inventory.orderType === OrderType.Now &&
      new Date(inventory.displayFromDateUtc || inventory.availableFromDateUtc)
        .toISOString()
        .split('T')[0] > new Date(getUTCNow()).toISOString().split('T')[0]
    );
  });

export const filterOutInventoriesBasedOnRule = (
  cellInventory: CellInventory,
  cellTotalsFilter: string,
  selectedOrderType: OrderType,
  nowInventoryLeadTimeInDays = 0,
): number => {
  if (!cellTotalsFilter) {
    return cellInventory.totals;
  }

  if (
    cellTotalsFilter.toUpperCase() === OrderType.Now &&
    selectedOrderType === OrderType.Now
  ) {
    const filtered = getTotals(
      cellInventory.inventories.filter(
        (inventory) =>
          !(
            inventory.orderType === OrderType.Now &&
            // we need to check availability w/o actual hours
            // because NOW we take as immediate time
            new Date(
              (inventory.availableFromDateUtc ||
                inventory.displayFromDateUtc) as string,
            ).toDateString() ===
              new Date(
                getUTCNowPlusDays(nowInventoryLeadTimeInDays),
              ).toDateString()
          ),
      ),
    );
    return cellInventory.totals - filtered;
  }

  return cellInventory.totals;
};

export const getTotals = (inventories: InventoryItemFragment[]): number => {
  return inventories.reduce((acc, inventory) => {
    let quantity = inventory.quantity;

    if (quantity === null) {
      quantity = Infinity;
    } else if (quantity === undefined || isNaN(+quantity)) {
      quantity = 0;
    }

    return acc + quantity;
  }, 0);
};

export const splitTotals = (quantity: number, cellInventory: CellInventory) => {
  if (!cellInventory.hasTrueDemandInventory) {
    return [quantity];
  }

  const totals = getTotals(cellInventory.inventories);

  return quantity > totals ? [totals, quantity - totals] : [quantity];
};

/**
 * availabilityColorRanges contains a list of ranges (start;end)
 * which are used to apply custom css classes (e.g. text-very-low-amount).
 * THey are configured via rule config-strip > availability-ranges
 * The colors are hardcoded in brand colors (e.g. very-low-amount).
 * If quantity is beetween min and max, we return the corrispondent color class.
 *
 */
export const getCellColor = (
  isLocked: boolean,
  totals: number,
  availabilityColorRanges: AvailabilityColorRange[],
  prefix = 'text-',
): string => {
  if (isLocked) {
    return '';
  }

  if (totals) {
    const range = availabilityColorRanges?.find((entry) => {
      return totals >= entry.start && entry.end >= totals;
    });
    return range?.color ? `${prefix}${range.color}` : '';
  }

  return '';
};
